import { useState } from "react";
import { useScrollAnimation } from "../../../hooks/useScrollAnimation";

export default function CustomJewelrySection() {
  const { ref, isVisible } = useScrollAnimation<HTMLDivElement>();
  const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    piece_type: "",
    details: "",
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (formData.details.length > 500) return;
    setFormStatus("submitting");
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => body.append(k, v));
      const res = await fetch("https://readdy.ai/api/form/d6utje5t4s7j0tv5i9dg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (res.ok) {
        setFormStatus("success");
        setFormData({ name: "", email: "", piece_type: "", details: "" });
      } else {
        setFormStatus("error");
      }
    } catch {
      setFormStatus("error");
    }
  };

  return (
    <section id="custom" className="relative py-20 md:py-40 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/604df9abddc33732e409b2626b460fc8.jpg"
          alt="Custom jewelry background"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#6B4423]/90 via-[#6B4423]/80 to-[#8B5A3C]/85" />
      </div>

      <div className="relative z-10 max-w-[1200px] mx-auto px-5 sm:px-8 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-20 items-center">
          {/* Left – Text */}
          <div
            ref={ref}
            className={`transition-all duration-1000 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
            }`}
          >
            <p
              className="text-[#D4AF87]/80 text-[11px] uppercase tracking-[4px] mb-5 sm:mb-6"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              Bespoke Service
            </p>
            <h2
              className="text-white text-4xl sm:text-5xl leading-tight mb-6 sm:mb-8"
              style={{ fontFamily: "Cinzel, serif", fontWeight: 400 }}
            >
              Made Just For You
            </h2>
            <p
              className="text-white/85 text-base sm:text-lg leading-relaxed mb-8 sm:mb-10"
              style={{ fontFamily: "Inter, sans-serif", fontWeight: 300 }}
            >
              We create exclusive handcrafted pieces designed uniquely for each
              client. A collaboration between artisan and soul — your vision,
              our craft.
            </p>
            <div className="grid grid-cols-2 gap-4 sm:gap-6">
              {[
                { label: "Consultation", desc: "Personal 1-on-1 design session" },
                { label: "Handcrafted", desc: "Individually made by master artisans" },
                { label: "Lifetime Care", desc: "Complimentary maintenance forever" },
                { label: "Your Story", desc: "Engraving and personalization included" },
              ].map((item) => (
                <div key={item.label} className="border border-[#D4AF87]/25 p-4 sm:p-5 rounded-sm">
                  <p
                    className="text-[#D4AF87] text-xs uppercase tracking-[2px] mb-1 sm:mb-2"
                    style={{ fontFamily: "Inter, sans-serif" }}
                  >
                    {item.label}
                  </p>
                  <p
                    className="text-white/70 text-xs sm:text-sm"
                    style={{ fontFamily: "Inter, sans-serif", fontWeight: 300 }}
                  >
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Right – Form */}
          <div
            className={`bg-[#FAF8F3] p-7 sm:p-10 rounded-lg transition-all duration-1000 delay-300 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
            }`}
          >
            <h3
              className="text-[#6B4423] text-xl sm:text-2xl mb-6 sm:mb-8"
              style={{ fontFamily: "Cinzel, serif", fontWeight: 400 }}
            >
              Request Your Custom Piece
            </h3>

            {formStatus === "success" ? (
              <div className="text-center py-12">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-3xl text-[#B76E79]" />
                </div>
                <p
                  className="text-[#6B4423] text-lg mb-2"
                  style={{ fontFamily: "Playfair Display, serif" }}
                >
                  Request Received
                </p>
                <p
                  className="text-[#7A6F5D] text-sm"
                  style={{ fontFamily: "Inter, sans-serif" }}
                >
                  We&apos;ll be in touch within 48 hours to begin your journey.
                </p>
              </div>
            ) : (
              <form
                id="custom-jewelry-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div>
                  <label
                    className="block text-[#6B4423] text-xs uppercase tracking-[2px] mb-2"
                    style={{ fontFamily: "Inter, sans-serif" }}
                  >
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full border-b border-[#7A6F5D]/30 bg-transparent py-3 text-sm text-[#3D2817] focus:outline-none focus:border-[#B76E79] transition-colors duration-300"
                    style={{ fontFamily: "Inter, sans-serif" }}
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label
                    className="block text-[#6B4423] text-xs uppercase tracking-[2px] mb-2"
                    style={{ fontFamily: "Inter, sans-serif" }}
                  >
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full border-b border-[#7A6F5D]/30 bg-transparent py-3 text-sm text-[#3D2817] focus:outline-none focus:border-[#B76E79] transition-colors duration-300"
                    style={{ fontFamily: "Inter, sans-serif" }}
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <label
                    className="block text-[#6B4423] text-xs uppercase tracking-[2px] mb-2"
                    style={{ fontFamily: "Inter, sans-serif" }}
                  >
                    Type of Piece
                  </label>
                  <select
                    name="piece_type"
                    required
                    value={formData.piece_type}
                    onChange={(e) => setFormData({ ...formData, piece_type: e.target.value })}
                    className="w-full border-b border-[#7A6F5D]/30 bg-transparent py-3 text-sm text-[#3D2817] focus:outline-none focus:border-[#B76E79] transition-colors duration-300 cursor-pointer"
                    style={{ fontFamily: "Inter, sans-serif" }}
                  >
                    <option value="">Select a type</option>
                    <option value="Ring">Ring</option>
                    <option value="Necklace">Necklace</option>
                    <option value="Bracelet">Bracelet</option>
                    <option value="Earrings">Earrings</option>
                    <option value="Set">Complete Set</option>
                  </select>
                </div>
                <div>
                  <label
                    className="block text-[#6B4423] text-xs uppercase tracking-[2px] mb-2"
                    style={{ fontFamily: "Inter, sans-serif" }}
                  >
                    Your Vision ({formData.details.length}/500)
                  </label>
                  <textarea
                    name="details"
                    required
                    maxLength={500}
                    value={formData.details}
                    onChange={(e) => setFormData({ ...formData, details: e.target.value })}
                    rows={3}
                    className="w-full border-b border-[#7A6F5D]/30 bg-transparent py-3 text-sm text-[#3D2817] focus:outline-none focus:border-[#B76E79] transition-colors duration-300 resize-none"
                    style={{ fontFamily: "Inter, sans-serif" }}
                    placeholder="Describe your vision, occasion, or inspiration..."
                  />
                </div>
                <button
                  type="submit"
                  disabled={formStatus === "submitting" || formData.details.length > 500}
                  className="w-full py-4 bg-[#6B4423] text-[#D4AF87] text-xs uppercase tracking-[2px] hover:bg-[#8B5A3C] transition-colors duration-300 cursor-pointer disabled:opacity-60 whitespace-nowrap mt-2"
                  style={{ fontFamily: "Inter, sans-serif", borderRadius: "2px" }}
                >
                  {formStatus === "submitting" ? "Sending..." : "Request Your Custom Piece"}
                </button>
                {formStatus === "error" && (
                  <p className="text-red-500 text-xs text-center" style={{ fontFamily: "Inter, sans-serif" }}>
                    Something went wrong. Please try again.
                  </p>
                )}
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
